import { Edges, useGLTF } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Surface3D } from '../surfaces/Surface3D'
import { plateIdForSelection } from '@asset-viz/utils/plateSelection'
import {
  overlayMaterialDefines,
  RenderLayerID,
  RenderOrder,
} from '@asset-viz/utils/renderingDefines'
import React from 'react'
import * as THREE from 'three'
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentCoords,
  GeckoComponentRect,
  GeckoMeasurementUnit,
  GeckoPolygonalPlate,
  GeckoShipHull,
} from 'types'
import {
  collectGLTFMeshes,
  getPlateCameraFocus,
  isSelectedPlateMesh,
} from './gltfMeshes'

const PLATE_FOCUS_DURATION_SECONDS = 0.65
const PLATE_FOCUS_PADDING = 1.35

interface GLTFModelProps {
  url: string
  controlsRef: React.RefObject<OrbitControlsImpl | null>
  onBoundsReady?: (bbox: THREE.Box3) => void
}

export const GLTFModel: React.FC<GLTFModelProps> = ({
  url,
  controlsRef,
  onBoundsReady,
}) => {
  const {
    assetModel,
    focusedCDF,
    focusedComponentID,
    selection,
    selectionColorString,
  } = useAssetVizInternal()
  const { camera } = useThree()
  const { scene } = useGLTF(url)
  const { surfaces, context, bbox, usesMarkedShells } = React.useMemo(
    () => collectGLTFMeshes(scene),
    [scene]
  )
  const bounds = React.useMemo(() => {
    if (bbox.isEmpty()) return null
    return (
      focusedCDF?.data.geometry ??
      GeckoComponentRect.CreateFromXYs(
        focusedCDF?.tags.component ?? surfaces[0]?.name ?? 'model',
        bbox.min.x,
        bbox.min.y,
        bbox.max.x,
        bbox.max.y,
        focusedCDF?.data.params.measurement_unit ?? GeckoMeasurementUnit.Feet,
        focusedCDF?.data.params.measurement_unit ?? GeckoMeasurementUnit.Feet
      )
    )
  }, [focusedCDF, surfaces, bbox])
  const selectedPlateID = React.useMemo(() => {
    const componentModel = assetModel?.data.components.get(focusedComponentID)
    if (!(componentModel instanceof GeckoShipHull)) return undefined
    const plateSelection =
      selection instanceof GeckoComponentCoords ||
      selection instanceof GeckoComponentRect ||
      selection instanceof GeckoPolygonalPlate
        ? selection
        : undefined
    return plateIdForSelection(plateSelection, componentModel.polygonalPlates)
  }, [assetModel, focusedComponentID, selection])
  const cameraAnimation = React.useRef<{
    elapsed: number
    startPosition: THREE.Vector3
    startTarget: THREE.Vector3
    targetPosition: THREE.Vector3
    target: THREE.Vector3
  }>()

  React.useEffect(() => {
    const controls = controlsRef.current
    if (
      !selectedPlateID ||
      !controls ||
      !(camera instanceof THREE.PerspectiveCamera)
    ) {
      return
    }
    const focus = getPlateCameraFocus(surfaces, selectedPlateID, bbox)
    if (!focus) return

    const sphere = focus.bounds.getBoundingSphere(new THREE.Sphere())
    const verticalHalfFov = THREE.MathUtils.degToRad(camera.fov / 2)
    const horizontalHalfFov = Math.atan(
      Math.tan(verticalHalfFov) * camera.aspect
    )
    const limitingHalfFov = Math.min(verticalHalfFov, horizontalHalfFov)
    const distance =
      (sphere.radius / Math.sin(limitingHalfFov)) * PLATE_FOCUS_PADDING
    const targetPosition = focus.center
      .clone()
      .addScaledVector(focus.outwardNormal, distance)

    cameraAnimation.current = {
      elapsed: 0,
      startPosition: camera.position.clone(),
      startTarget: controls.target.clone(),
      targetPosition,
      target: focus.center,
    }
  }, [bbox, camera, controlsRef, selectedPlateID, surfaces])

  useFrame((_, delta) => {
    const animation = cameraAnimation.current
    const controls = controlsRef.current
    if (!animation || !controls) return

    animation.elapsed += delta
    const progress = Math.min(
      animation.elapsed / PLATE_FOCUS_DURATION_SECONDS,
      1
    )
    const easedProgress = 1 - (1 - progress) ** 3
    camera.position.lerpVectors(
      animation.startPosition,
      animation.targetPosition,
      easedProgress
    )
    controls.target.lerpVectors(
      animation.startTarget,
      animation.target,
      easedProgress
    )
    controls.update()
    if (progress === 1) cameraAnimation.current = undefined
  })

  React.useEffect(() => {
    if (!bbox.isEmpty()) onBoundsReady?.(bbox)
  }, [bbox, onBoundsReady])
  if (!bounds) return null
  return (
    <group dispose={null}>
      {context.map((mesh) => (
        <mesh
          key={mesh.uuid}
          name={mesh.name}
          geometry={mesh.geometry}
          material={mesh.material}
          matrix={mesh.matrixWorld}
          matrixAutoUpdate={false}
          userData={mesh.userData}
          layers={RenderLayerID.VisibleNonCollidable}
        />
      ))}
      {surfaces.map((mesh) => (
        <React.Fragment key={mesh.uuid}>
          <Surface3D
            name={mesh.name}
            userData={mesh.userData}
            matrix={mesh.matrixWorld}
            matrixAutoUpdate={false}
            geometry={mesh.geometry}
            bounds={bounds}
            baseMaterial={usesMarkedShells ? mesh.material : undefined}
            wrapMode={ComponentSurfaceWrappingMode.ComponentNoWrap}
          />
          {isSelectedPlateMesh(mesh, selectedPlateID) ? (
            <Edges
              name={`${mesh.name}-selection-outline`}
              geometry={mesh.geometry}
              matrix={mesh.matrixWorld}
              matrixAutoUpdate={false}
              threshold={180}
              linewidth={3}
              color={selectionColorString}
              renderOrder={RenderOrder.SelectedFeature}
              layers={RenderLayerID.VisibleNonCollidable}
              {...overlayMaterialDefines}
            />
          ) : null}
        </React.Fragment>
      ))}
    </group>
  )
}
